import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import { supabase } from '@/lib/supabase'
import type { AuthUser, Profile, UserRole } from '@/types'
import type { Session } from '@supabase/supabase-js'
import { useThemeStore } from './theme'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const session = ref<Session | null>(null)
  const loading = ref(true)
  const initialized = ref(false)
  const deactivationMessage = ref('')
  const isLoggingOut = ref(false) // Prevent watchers from restoring during logout

  const isAuthenticated = computed(() => {
    // During logout, always return false immediately
    if (isLoggingOut.value) {
      console.log('🚫 isAuthenticated check: logout in progress, returning false')
      return false
    }
    
    // Check both user and session to be more robust
    const hasUser = !!user.value
    const hasSession = !!session.value
    const isSessionValid = session.value && 
      (!session.value.expires_at || new Date(session.value.expires_at * 1000) > new Date())
    
    // User is authenticated if they have a valid session
    // During logout, isLoggingOut is checked first (above) to return false immediately
    // During login, session is set first, then profile loads - session is enough to authenticate
    // Once fully loaded, both user.profile and session will be present
    const isAuth = hasSession && isSessionValid && !isLoggingOut.value
    
    // Debug logging to diagnose authentication issue
    if (!isAuth && (hasUser || hasSession)) {
      console.log('❌ isAuthenticated returning FALSE despite user/session:', {
        hasUser,
        hasSession,
        isSessionValid,
        isAuth,
        isLoggingOut: isLoggingOut.value,
        userProfile: !!user.value?.profile,
        userEmail: user.value?.email,
        sessionExpiresAt: session.value?.expires_at,
        sessionUser: session.value?.user?.email
      })
    }
    
    return isAuth
  })
  const userRole = computed(() => user.value?.profile?.role)
  const isPlayer = computed(() => isAuthenticated.value && userRole.value === 'player')
  const isParentGuardian = computed(() => isAuthenticated.value && userRole.value === 'parent_guardian')
  const isCoach = computed(() => isAuthenticated.value && userRole.value === 'coach')
  const isClubAdmin = computed(() => isAuthenticated.value && userRole.value === 'club_admin')
  const isSystemAdmin = computed(() => isAuthenticated.value && userRole.value === 'system_admin')

  // Watch for authentication state changes and try to restore if needed
  // DISABLED: This watcher was causing race conditions during login
  // Session restoration is now handled explicitly by the auth state change listener
  // watch([user, session], async ([newUser, newSession]) => {
  //   // Don't attempt any restoration if we're in the process of logging out
  //   if (isLoggingOut.value) {
  //     console.log('🚫 Skipping session restoration - logout in progress')
  //     return
  //   }
  //   
  //   // If session exists but user is cleared, try to restore
  //   if (!newUser && newSession && newSession.user) {
  //     console.log('🔄 Detected session without user, attempting restore...')
  //     try {
  //       await restoreUserSession()
  //     } catch (error) {
  //       console.error('Failed to restore user session:', error)
  //     }
  //   }
  //   
  //   // If user exists but session is missing, try to get current session
  //   if (newUser && !newSession) {
  //     console.log('🔄 Detected user without session, attempting to get current session...')
  //     try {
  //       const { data: { session: currentSession } } = await supabase.auth.getSession()
  //       if (currentSession) {
  //         console.log('✅ Found current session, updating...')
  //         session.value = currentSession
  //       }
  //     } catch (error) {
  //       console.error('Failed to get current session:', error)
  //     }
  //   }
  // }, { immediate: false })

  // Restore user session if session exists but user is cleared
  const restoreUserSession = async () => {
    try {
      if (!user.value && session.value) {
        console.log('🔄 Restoring user session from existing session...')
        await setUserSession(session.value)
        return true
      }
      return false
    } catch (error) {
      console.error('Error restoring user session:', error)
      return false
    }
  }

  // Check if session is still valid
  const checkSessionValidity = async () => {
    try {
      console.log('🔍 Checking session validity...')
      const { data: { session: currentSession }, error } = await supabase.auth.getSession()
      
      console.log('🔍 Session check result:', {
        hasSession: !!currentSession,
        hasError: !!error,
        errorMessage: error?.message,
        expiresAt: currentSession?.expires_at,
        isLocalhost: window.location.hostname === 'localhost'
      })
      
      if (error) {
        console.log('⚠️ Session error detected:', error.message)
        // For localhost, be more lenient with certain errors
        if (window.location.hostname === 'localhost' && 
            (error.message.includes('network') || error.message.includes('fetch'))) {
          console.log('🔄 Localhost network error, attempting to refresh session...')
          const refreshResult = await refreshSession()
          if (refreshResult.error) {
            console.log('⚠️ Refresh failed, clearing session')
            clearUserSession()
            return false
          }
          return true
        }
        clearUserSession()
        return false
      }
      
      if (!currentSession) {
        console.log('⚠️ No session found, clearing user session')
        clearUserSession()
        return false
      }
      
      // Check if session is expired (expires_at is in seconds, convert to milliseconds)
      if (currentSession.expires_at && new Date(currentSession.expires_at * 1000) <= new Date()) {
        console.log('⚠️ Session has expired, clearing user session')
        clearUserSession()
        return false
      }
      
      // Update session if it's different from current
      if (currentSession !== session.value) {
        console.log('🔄 Session updated, refreshing user session')
        session.value = currentSession
        await setUserSession(currentSession)
      }
      
      // If user is cleared but session is valid, try to restore
      if (!user.value && currentSession) {
        console.log('🔄 User cleared but session valid, attempting restore...')
        const restored = await restoreUserSession()
        if (!restored) {
          console.log('⚠️ Failed to restore user session')
          return false
        }
      }
      
      console.log('✅ Session is valid')
      return true
    } catch (error) {
      console.error('Error checking session validity:', error)
      // For localhost, try to refresh before giving up
      if (window.location.hostname === 'localhost') {
        console.log('🔄 Localhost error, attempting refresh...')
        try {
          const refreshResult = await refreshSession()
          if (!refreshResult.error) {
            return true
          }
        } catch (refreshError) {
          console.error('Refresh also failed:', refreshError)
        }
      }
      clearUserSession()
      return false
    }
  }

  const refreshSession = async () => {
    try {
      console.log('🔄 Refreshing session to get updated JWT...')
      console.log('📡 About to call supabase.auth.refreshSession()...')
      
      // Add timeout to prevent hanging
      const refreshPromise = supabase.auth.refreshSession()
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Refresh session timeout')), 5000)
      )
      
      const { data: { session: refreshedSession }, error } = await Promise.race([
        refreshPromise,
        timeoutPromise
      ]) as any
      
      console.log('📡 supabase.auth.refreshSession() completed:', { 
        hasSession: !!refreshedSession, 
        error: error?.message || 'none' 
      })
      
      if (error) {
        console.error('Error refreshing session:', error)
        return { error }
      }
      
      if (refreshedSession) {
        console.log('✅ Got refreshed session, calling setUserSession...')
        await setUserSession(refreshedSession)
        console.log('✅ Session refreshed successfully with updated JWT claims:', {
          jwtUserRole: refreshedSession.user?.app_metadata?.user_role,
          isSystemAdmin: refreshedSession.user?.app_metadata?.user_role === 'system_admin'
        })
      } else {
        console.warn('⚠️ No session returned from refreshSession')
      }
      
      return { error: null }
    } catch (error) {
      console.error('Unexpected error refreshing session:', error)
      return { error }
    }
  }

  const initialize = async () => {
    try {
      loading.value = true
      
      // Get initial session
      const { data: { session: initialSession } } = await supabase.auth.getSession()
      
      if (initialSession) {
        await setUserSession(initialSession)
        
        // Debug logging for initialization
        console.log('🚀 Auth Store - Initialization complete:', {
          isAuthenticated: isAuthenticated.value,
          userRole: userRole.value,
          isSystemAdmin: isSystemAdmin.value,
          userProfile: user.value?.profile
        })
      }

      // Listen for auth changes
      supabase.auth.onAuthStateChange(async (event, newSession) => {
        console.log('🔄 Auth state change event:', { event, hasSession: !!newSession })
        
        if (event === 'SIGNED_IN' && newSession) {
          console.log('✅ User signed in, setting session')
          await setUserSession(newSession)
        } else if (event === 'SIGNED_OUT') {
          console.log('✅ User signed out, clearing session')
          clearUserSession()
        } else if (event === 'TOKEN_REFRESHED' && newSession) {
          console.log('🔄 Token refreshed, updating session')
          session.value = newSession
          // When token is refreshed, also update user profile to get latest data
          if (newSession.user) {
            const profile = await fetchUserProfile(newSession.user.id)
            if (user.value) {
              user.value.profile = profile
            }
          }
        } else if (!newSession) {
          // Handle session expiration or loss
          console.log('⚠️ Session expired or lost, clearing user session')
          clearUserSession()
        } else {
          console.log('ℹ️ Other auth event:', event)
        }
      })
    } catch (error) {
      console.error('Error initializing auth:', error)
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  const setUserSession = async (newSession: Session) => {
    console.log('🔧 setUserSession called with:', {
      hasUser: !!newSession.user,
      userEmail: newSession.user?.email,
      expiresAt: newSession.expires_at
    })
    
    // Ensure logout flag is cleared when setting a new session
    isLoggingOut.value = false
    console.log('✅ isLoggingOut cleared')
    
    session.value = newSession
    console.log('✅ session.value set')
    
    if (newSession.user) {
      console.log('🔍 Fetching user profile for:', newSession.user.id)
      // Fetch user profile using auth_user_id
      const profile = await fetchUserProfile(newSession.user.id)
      console.log('✅ Profile fetched:', {
        hasProfile: !!profile,
        profileId: profile?.id,
        profileRole: profile?.role
      })
      
      user.value = {
        id: profile?.id || newSession.user.id, // Use profile ID if available, fallback to auth user ID
        email: newSession.user.email!,
        profile
      }
      console.log('✅ user.value set:', {
        userId: user.value.id,
        userEmail: user.value.email,
        hasProfile: !!user.value.profile
      })
      
      // Remember that user was logged in for fallback
      localStorage.setItem('teamio_user_was_logged_in', 'true')
      console.log('✅ localStorage updated')
      
      // Update theme store with admin status
      const themeStore = useThemeStore()
      const isAdmin = profile?.role === 'system_admin'
      themeStore.setAdminStatus(isAdmin)
      console.log('✅ Theme store updated, isAdmin:', isAdmin)
      
      // Debug logging for role synchronization
      console.log('🔍 Auth Store - User session set:', {
        userId: newSession.user.id,
        profileId: profile?.id,
        email: newSession.user.email,
        profileRole: profile?.role,
        isSystemAdmin: profile?.role === 'system_admin',
        jwtUserRole: newSession.user.app_metadata?.user_role || 'NOT_SET',
        computedIsSystemAdmin: profile?.role === 'system_admin',
        jwtValid: !!newSession.access_token,
        sessionExpiry: new Date(newSession.expires_at! * 1000).toLocaleString()
      })
      
      // Check if JWT claim matches profile role
      if (profile?.role && newSession.user.app_metadata?.user_role !== profile.role) {
        console.warn('⚠️ JWT claim does not match profile role:', {
          profileRole: profile.role,
          jwtRole: newSession.user.app_metadata?.user_role || 'NOT_SET',
          userId: newSession.user.id,
          email: newSession.user.email,
          fullAppMetadata: newSession.user.app_metadata
        })
        console.log('💡 This may cause RLS issues. User may need to log out and back in.')
      } else if (profile?.role) {
        console.log('✅ JWT claim matches profile role correctly:', {
          role: profile.role,
          jwtClaim: newSession.user.app_metadata?.user_role,
          userId: newSession.user.id
        })
      }
    }
  }

  const clearUserSession = () => {
    // Set logout flag FIRST to prevent watchers from interfering
    isLoggingOut.value = true
    
    user.value = null
    session.value = null
    
    // Reset theme store admin status
    const themeStore = useThemeStore()
    themeStore.setAdminStatus(false)
    
    // Clear all Supabase auth data from storage
    // This ensures the session doesn't get restored on page reload
    const storageKeys = [
      'teamio_user_was_logged_in',
      'sb-gjzayyikxpngfhlisryz-auth-token'  // Supabase session storage key
    ]
    
    storageKeys.forEach(key => {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    })
    
    console.log('🧹 Cleared all auth storage')
    
    // Note: isLoggingOut will be cleared when setUserSession is called on next login
  }

  const fetchUserProfile = async (authUserId: string): Promise<Profile | undefined> => {
    try {
      console.log('🔍 Fetching user profile for auth user ID:', authUserId)
      
      // Query by auth_user_id instead of profiles.id
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('auth_user_id', authUserId)
        .single()

      if (error) {
        console.error('❌ Error fetching profile:', {
          error: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          authUserId,
          supabaseUrl: import.meta.env.VITE_SUPABASE_URL || 'MISSING',
          hasAnonKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY
        })
        
        // Check if this is a connection error
        if (error.message.includes('Failed to fetch') || error.message.includes('fetch')) {
          console.error('🚨 CONNECTION ERROR: Cannot reach Supabase. Check:')
          console.error('1. VITE_SUPABASE_URL is correct:', import.meta.env.VITE_SUPABASE_URL)
          console.error('2. VITE_SUPABASE_ANON_KEY is set:', !!import.meta.env.VITE_SUPABASE_ANON_KEY)
          console.error('3. Network connectivity to Supabase')
          console.error('4. Supabase project is active and accessible')
        }
        
        return undefined
      }

      console.log('✅ Successfully fetched user profile:', data)
      return data || undefined
    } catch (error) {
      console.error('❌ Unexpected error fetching profile:', {
        error: error instanceof Error ? error.message : error,
        authUserId,
        errorType: typeof error,
        errorConstructor: error?.constructor?.name
      })
      
      // Additional debugging for network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('🚨 NETWORK ERROR: This appears to be a connection issue with Supabase')
        console.error('Please verify your .env file contains the correct Supabase credentials')
      }
      
      return undefined
    }
  }

  const signUp = async (email: string, password: string, userData: Partial<Profile>) => {
    try {
      loading.value = true

      // Check if this is a U16 player registration
      const isU16Player = userData.date_of_birth && 
        new Date().getFullYear() - new Date(userData.date_of_birth).getFullYear() < 16

      if (isU16Player && userData.role === 'player') {
        // For U16 players, we need a parent/guardian to create their profile
        // This should be handled differently - perhaps through a separate flow
        throw new Error('U16 players must be registered by a parent/guardian')
      }

      // Standard user registration
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: userData.first_name || '',
            last_name: userData.last_name || '',
            role: userData.role || 'player',
            phone: userData.phone || null,
            county_fa_region: userData.county_fa_region || null,
            club_affiliation: userData.club_affiliation || null,
            date_of_birth: userData.date_of_birth || null,
            preferred_position: userData.preferred_position || null,
            secondary_positions: userData.secondary_positions || null
          }
        }
      })

      if (error) throw error

      // The handle_new_user trigger will automatically create the profile
      // No need to manually create profile for authenticated users

      // Get the session after signup
      if (data.user) {
        const { data: { session: newSession } } = await supabase.auth.getSession()
        if (newSession) {
          await setUserSession(newSession)
        }
      }

      return { data, error: null }
    } catch (error) {
      console.error('Sign up error:', error)
      return { data: null, error }
    } finally {
      loading.value = false
    }
  }

  const createU16PlayerProfile = async (playerData: {
    date_of_birth: string
    gender_identity?: string
    skill_level?: string
    county_fa_region?: string
    experience?: string
    playing_position?: string[] | null
  }) => {
    try {
      console.log('🔍 createU16PlayerProfile called with:', {
        userRole: user.value?.profile?.role,
        sessionUserId: session.value?.user?.id,
        userEmail: session.value?.user?.email,
        profileId: user.value?.profile?.id,
        playerData
      })

      if (!user.value?.profile?.role || !['parent_guardian', 'system_admin'].includes(user.value.profile.role)) {
        throw new Error('Only parents/guardians can create U16 player profiles')
      }

      if (!session.value?.user?.id) {
        throw new Error('No active session user ID')
      }

      // Generate anonymized names on the backend - parents cannot provide real names
      const timestamp = Date.now()
      const randomId = Math.random().toString(36).substring(2, 8)
      const anonymizedFirstName = `Player_${randomId}`
      const anonymizedLastName = `${timestamp}`

      console.log('🔍 Calling create_u16_player_profile RPC with anonymized names:', {
        p_parent_guardian_auth_id: session.value.user.id,
        p_first_name: anonymizedFirstName,
        p_last_name: anonymizedLastName,
        p_date_of_birth: playerData.date_of_birth,
        p_gender_identity: playerData.gender_identity,
        p_sex: null,
        p_playing_position: playerData.playing_position,
        p_skill_level: playerData.skill_level,
        p_county_fa_region: playerData.county_fa_region,
        p_experience: playerData.experience
      })

      // Use named parameters for PostgREST to avoid 400 "All object keys must match"
      const { data, error } = await supabase.rpc('create_u16_player_profile', {
        p_parent_guardian_auth_id: session.value.user.id,
        p_first_name: anonymizedFirstName,
        p_last_name: anonymizedLastName,
        p_date_of_birth: playerData.date_of_birth,
        p_gender_identity: playerData.gender_identity || null,
        p_sex: null,
        p_playing_position: playerData.playing_position,
        p_skill_level: playerData.skill_level || null,
        p_county_fa_region: playerData.county_fa_region || null,
        p_experience: playerData.experience || null
      })

      console.log('🔍 create_u16_player_profile RPC result:', { data, error })

      if (error) {
        console.error('❌ RPC error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          fullError: error
        })
        return { data: null, error }
      }

      return { data, error: null }
    } catch (error) {
      console.error('❌ Create U16 player profile error:', error)
      return { data: null, error }
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      loading.value = true
      deactivationMessage.value = '' // Clear any previous deactivation message

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error

      // Check if user is deactivated
      if (data.user?.user_metadata?.disabled === true) {
        console.log('🚫 User account is deactivated, preventing login')
        
        // Immediately sign out the user
        await supabase.auth.signOut()
        
        // Set the deactivation message
        deactivationMessage.value = 'Your account is not currently active. Please contact support@teamio.co.uk for assistance.'
        
        // Clear local session state
        clearUserSession()
        
        // Return success but with deactivated user (handled by UI)
        return { data, error: null }
      }
      return { data, error: null }
    } catch (error) {
      console.error('Sign in error:', error)
      return { data: null, error }
    } finally {
      loading.value = false
    }
  }

  const signOut = async () => {
    try {
      loading.value = true
      
      console.log('🚪 Signing out user...')

      // Clear local session state FIRST to ensure UI updates immediately
      clearUserSession()
      // Clear localStorage when actually signing out
      localStorage.removeItem('teamio_user_was_logged_in')
      console.log('✅ Local session state cleared immediately')
      
      let signOutError = null
      
      try {
        const { error } = await supabase.auth.signOut()
        signOutError = error
        
        if (error) {
          console.warn('⚠️ Supabase sign out returned error:', error)
        } else {
          console.log('✅ Supabase sign out successful')
        }
      } catch (unexpectedError) {
        console.error('❌ Unexpected error during Supabase sign out:', unexpectedError)
        signOutError = unexpectedError
      }
      
      // Force a small delay to ensure auth state change is processed
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // If Supabase sign out failed, force a page reload as fallback
      if (signOutError) {
        console.warn('⚠️ Supabase sign out failed, forcing page reload to ensure clean state')
        window.location.reload()
        return { error: signOutError }
      }
      
      console.log('✅ Sign out completed successfully')
      return { error: null }
    } catch (error) {
      console.error('Sign out error:', error)
      // Even if there's an outer error, still clear local state
      clearUserSession()
      return { error }
    } finally {
      loading.value = false
    }
  }

  const forceSessionRefresh = async () => {
    try {
      console.log('🔄 Force refreshing session to get updated JWT...')
      
      // Sign out first to clear any cached tokens
      await supabase.auth.signOut()
      
      // Clear local state
      clearUserSession()
      
      console.log('✅ Session cleared - user needs to log in again for fresh JWT')
      
      return { error: null }
    } catch (error) {
      console.error('Error force refreshing session:', error)
      return { error }
    }
  }

  const updateUserPhone = async (phone: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ phone })
      
      if (error) throw error
      
      return { error: null }
    } catch (error) {
      console.error('Update mobile error:', error)
      return { error }
    }
  }

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      })

      if (error) throw error

      return { error: null }
    } catch (error) {
      console.error('Reset password error:', error)
      return { error }
    }
  }

  const updatePassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password })

      if (error) throw error

      return { error: null }
    } catch (error) {
      console.error('Update password error:', error)
      return { error }
    }
  }

  const updateProfile = async (updates: Partial<Profile>) => {
    try {
      console.log('🔄 Auth store updateProfile started...')
      if (!user.value) throw new Error('No user logged in')

      // Use the profile ID for updates (not the auth user ID)
      const profileId = user.value.profile?.id || user.value.id
      console.log('📝 Updating profile with ID:', profileId, 'Updates:', updates)

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', profileId)
        .select()
        .single()

      console.log('📡 Supabase response:', { data, error })

      if (error) throw error

      // Update local user data
      if (user.value.profile) {
        user.value.profile = { ...user.value.profile, ...data }
      } else {
        user.value.profile = data
      }

      console.log('✅ Profile update completed successfully')
      return { data, error: null }
    } catch (error) {
      console.error('❌ Update profile error:', error)
      return { data: null, error }
    }
  }

  const hasRole = (role: UserRole): boolean => {
    return userRole.value === role
  }

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return userRole.value ? roles.includes(userRole.value) : false
  }

  return {
    // State
    user: readonly(user),
    session: readonly(session),
    loading: readonly(loading),
    initialized: readonly(initialized),
    deactivationMessage: readonly(deactivationMessage),
    
    // Computed
    isAuthenticated,
    userRole,
    isPlayer,
    isParentGuardian,
    isCoach,
    isClubAdmin,
    isSystemAdmin,
    
    // Actions
    initialize,
    signUp,
    createU16PlayerProfile,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    hasRole,
    hasAnyRole,
    refreshSession,
    forceSessionRefresh,
    updateUserPhone,
    checkSessionValidity,
    restoreUserSession,
    clearUserSession
  }
}, {
  persist: {
    key: 'teamio-auth',
    paths: ['user', 'session']
  }
})